// What a workflow block or trigger needs from its location, and what the builder does when the
// location lacks it. Product missing = padlock (not bought), feature toggle off = hidden (not
// released), PMS connected but unable = hidden, no PMS yet = padlock (connecting one fixes it).
import type { PmsCapability } from 'app/types/pms'
import type { BlockGate, BlockedGate, GateLocation, GateSubject, GateToggles } from 'app/types/workflow-gates'
import { PMS_SUPPORT } from 'lib/pmsCapabilities'
import { pmsDisplayName } from 'lib/pmsNames'
import type { TriggerKind } from './triggerCatalog'
import type { WorkflowNode, WorkflowNodeKind } from 'app/types/workflows'
import type { WorkflowTemplate } from './workflowTemplates'

type Requirement = {
  toggle?: keyof GateToggles
  product?: string
  /** `any` = some PMS connected; a capability = a PMS that can serve it. */
  pms?: `any` | PmsCapability
}

const NODE_REQUIREMENTS: Partial<Record<Exclude<WorkflowNodeKind, `trigger`>, Requirement>> = {
  aiAgentSms: { toggle: `schedulingAgent`, product: `SMS Agent` },
  sendSms: { product: `Inbox` },
  sendPaperwork: { toggle: `paperwork`, product: `Inbox` },
  updateAppointment: { pms: `appointmentWriteback` },
}

const TRIGGER_REQUIREMENTS: Partial<Record<TriggerKind, Requirement>> = {
  call_finished_processing: { product: `Call Processing` },
  appointment_created: { pms: `any` },
  appointment_updated: { pms: `any` },
  appointment_date_reached: { pms: `any` },
  treatment_plan_updated: { pms: `unscheduledTreatment` },
  paperwork_completed: { toggle: `paperwork` },
}

const AVAILABLE: BlockGate = { state: `available` }

export function gateFor(subject: GateSubject, location: GateLocation | undefined, toggles: GateToggles): BlockGate {
  const need = `node` in subject ? NODE_REQUIREMENTS[subject.node] : TRIGGER_REQUIREMENTS[subject.trigger]
  if (!need) return AVAILABLE
  if (need.toggle && !toggles[need.toggle]) return { state: `hidden`, why: `toggle` }
  if (!location) return AVAILABLE
  const capability = need.pms && need.pms !== `any` ? need.pms : undefined
  if (location.pmsIntegrated && capability && !PMS_SUPPORT[capability](location)) return { state: `hidden`, why: `pms` }
  if (need.product && !location.products?.includes(need.product))
    return { state: `locked`, why: `product`, product: need.product }
  if (need.pms && !location.pmsIntegrated) return { state: `locked`, why: `no_pms` }
  return AVAILABLE
}

export function nodeGates(
  nodes: WorkflowNode[],
  location: GateLocation | undefined,
  toggles: GateToggles,
): Map<string, BlockedGate> {
  const gates = new Map<string, BlockedGate>()
  for (const node of nodes) {
    const subject: GateSubject =
      node.type === `trigger`
        ? { trigger: node.data.kind }
        : { node: node.type as Exclude<WorkflowNodeKind, `trigger`> }
    const gate = gateFor(subject, location, toggles)
    if (gate.state !== `available`) gates.set(node.id, gate)
  }
  return gates
}

const PMS_CANNOT: Record<string, string> = {
  updateAppointment: `confirm appointments`,
  treatment_plan_updated: `send treatment plan updates`,
}

/** `subjectKey` is the node kind or trigger kind, which picks the verb for a PMS that can't. */
export function gateMessage(
  gate: BlockedGate,
  blockLabel: string,
  location: GateLocation | undefined,
  subjectKey?: string,
): string {
  if (gate.state === `locked` && gate.why === `product`)
    return `${blockLabel} is part of ${gate.product}, which ${location?.name ?? `this location`} doesn't have.`
  if (gate.state === `locked`) return `${blockLabel} needs your practice system connected.`
  if (gate.why === `toggle`) return `${blockLabel} isn't released for this workspace yet.`
  const verb = (subjectKey && PMS_CANNOT[subjectKey]) || `run ${blockLabel}`
  return `${pmsDisplayName(location?.pms, `Your practice system`)} can't ${verb}.`
}

/** The gate on a whole template across the workspace's locations. Undefined while any location can
 *  run every step. At one location a hidden step outranks a padlocked one (buying can't fix it);
 *  across locations a padlock wins over hidden, because somewhere it can still be bought. */
export function templateLock(
  template: WorkflowTemplate,
  locations: GateLocation[],
  toggles: GateToggles,
): BlockedGate | undefined {
  const nodes = template.build?.().nodes
  if (!nodes || locations.length === 0) return undefined
  const perLocation: BlockedGate[] = []
  for (const location of locations) {
    const gates = [...nodeGates(nodes, location, toggles).values()]
    if (gates.length === 0) return undefined
    perLocation.push(gates.find((gate) => gate.state === `hidden`) ?? gates[0])
  }
  return perLocation.find((gate) => gate.state === `locked`) ?? perLocation[0]
}
